// Slide-out notifications panel triggered from the header bell.
// Spec: docs/specs/FEATURES-PHASE-4.md (Notifications panel)

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useTranslation } from '../../i18n';

const TYPE_ICONS = {
  approval_needed:      '📋',
  deadline_approaching: '⏰',
  content_due:          '📅',
  sync_complete:        '🔄',
  engagement_reminder:  '🤝',
  system_alert:         '⚠️',
};

const MS_IN_DAY = 24 * 60 * 60 * 1000;

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function groupNotifications(notifications, now = new Date()) {
  const todayStart = startOfDay(now).getTime();
  const weekStart  = todayStart - 6 * MS_IN_DAY; // last 7 days including today

  const today = [];
  const week  = [];
  const older = [];
  for (const n of notifications) {
    const created = new Date(n.createdAt).getTime();
    if (created >= todayStart) today.push(n);
    else if (created >= weekStart) week.push(n);
    else older.push(n);
  }
  return { today, week, older };
}

function formatTimeAgo(iso, t, now = new Date()) {
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return '';
  const diffMs = Math.max(0, now.getTime() - created);
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60)            return t('notifications.timeAgo.justNow');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)            return t('notifications.timeAgo.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24)              return t('notifications.timeAgo.hours', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7)                return t('notifications.timeAgo.days', { count: days });
  const weeks = Math.floor(days / 7);
  if (weeks < 4)               return t('notifications.timeAgo.weeks', { count: weeks });
  const months = Math.floor(days / 30);
  if (months < 12)             return t('notifications.timeAgo.months', { count: months });
  const years = Math.floor(days / 365);
  return t('notifications.timeAgo.years', { count: years });
}

function resourceHref(notification) {
  const { resourceType, resourceId } = notification;
  if (!resourceType || !resourceId) return null;
  if (resourceType === 'event') return `/events/${resourceId}`;
  if (resourceType === 'draft') return `/drafts/${resourceId}`;
  return null;
}

export function NotificationPanel({ open, onClose, onChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.notifications
      .list()
      .then((data) => {
        if (cancelled) return;
        setNotifications(Array.isArray(data?.notifications) ? data.notifications : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : t('notifications.loadError'));
        setNotifications([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  useEffect(() => {
    if (!open) return undefined;
    function handleKey(event) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const groups = groupNotifications(notifications);
  const hasAny = notifications.length > 0;
  const hasUnread = notifications.some((n) => !n.read);

  async function handleMarkAllRead() {
    if (!hasUnread) return;
    try {
      await api.notifications.markAllRead();
      setNotifications((prev) =>
        prev.map((n) =>
          n.read ? n : { ...n, read: true, readAt: new Date().toISOString() },
        ),
      );
      onChange?.();
    } catch {
      setError(t('notifications.markAllError'));
    }
  }

  async function handleClick(notification) {
    if (!notification.read) {
      try {
        await api.notifications.markRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id
              ? { ...n, read: true, readAt: new Date().toISOString() }
              : n,
          ),
        );
        onChange?.();
      } catch {
        // Soft fail — still allow navigation.
      }
    }
    const href = resourceHref(notification);
    onClose();
    if (href) navigate(href);
  }

  return (
    <div
      className="notif-panel-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        className="notif-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t('notifications.title')}
      >
        <header className="notif-panel__header">
          <h2 className="notif-panel__title">{t('notifications.title')}</h2>
          <div className="notif-panel__header-actions">
            <button
              type="button"
              className="notif-panel__mark-all"
              onClick={handleMarkAllRead}
              disabled={!hasUnread}
            >
              {t('notifications.markAllRead')}
            </button>
            <button
              type="button"
              className="notif-panel__close"
              onClick={onClose}
              aria-label={t('common.close')}
            >
              ×
            </button>
          </div>
        </header>

        <div className="notif-panel__body">
          {loading && (
            <p className="notif-panel__status">{t('notifications.loading')}</p>
          )}

          {!loading && error && (
            <p className="notif-panel__status notif-panel__status--error">{error}</p>
          )}

          {!loading && !error && !hasAny && (
            <p className="notif-panel__status">{t('notifications.empty')}</p>
          )}

          {!loading && !error && hasAny && (
            <>
              <NotificationGroup
                label={t('notifications.groups.today')}
                items={groups.today}
                onSelect={handleClick}
                t={t}
              />
              <NotificationGroup
                label={t('notifications.groups.thisWeek')}
                items={groups.week}
                onSelect={handleClick}
                t={t}
              />
              <NotificationGroup
                label={t('notifications.groups.older')}
                items={groups.older}
                onSelect={handleClick}
                t={t}
              />
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function NotificationGroup({ label, items, onSelect, t }) {
  if (!items.length) return null;
  return (
    <section className="notif-group">
      <h3 className="notif-group__label">{label}</h3>
      <ul className="notif-group__list">
        {items.map((n) => (
          <li key={n.id}>
            <button
              type="button"
              className={`notif-item${n.read ? '' : ' notif-item--unread'}`}
              onClick={() => onSelect(n)}
            >
              <span className="notif-item__icon" aria-hidden="true">
                {TYPE_ICONS[n.type] ?? '🔔'}
              </span>
              <span className="notif-item__main">
                <span className="notif-item__title">{n.title}</span>
                {n.body && <span className="notif-item__body">{n.body}</span>}
                <span className="notif-item__time">{formatTimeAgo(n.createdAt, t)}</span>
              </span>
              {!n.read && (
                <span
                  className="notif-item__dot"
                  aria-label={t('notifications.unread')}
                />
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
